import React, {FC, useEffect, useState} from 'react';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Pause from '@mui/icons-material/Pause';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import PlayArrow from '@mui/icons-material/PlayArrow';
import {styled} from '@mui/material/styles';

const classes = {
  root: 'animatedSlider-root',
  slider: 'animatedSlider-slider',
  range: 'animatedSlider-range',
  textRange: 'animatedSlider-textRange',
  iconRange: 'animatedSlider-iconRange'
};

const Root = styled(Box)(({theme}) => {
  return {
    '&.animatedSlider-root': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '21px'
    },
    '& .animatedSlider-slider': {},
    '& .animatedSlider-range': {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center'
    },
    '& .animatedSlider-iconRange': {},
    '& .animatedSlider-textRange': {
      color: theme.palette.text.primary
    }
  };
});

type animatedSliderProps = {
  value: number,
  min: number,
  max: number,
  animationInterval?: number,
  sx?: object,
  onValueChange: (value: number) => void
}

const AnimatedSlider: FC<animatedSliderProps> = ({
  value,
  min,
  max,
  animationInterval = 500,
  sx,
  onValueChange
}) => {
  const handleChange = (_: unknown, newValue: number | number[]) => {
    if (newValue instanceof Array) {
      onValueChange(newValue[0]);
    } else {
      onValueChange(newValue);
    }
  };

  const [isAnimating, setAnimating] = useState(false);

  const toggleAnimation = () => {
    setAnimating(!isAnimating);
  };

  useEffect(() => {
    let interval: number | undefined = undefined;
    if (isAnimating) {
      interval = window.setInterval(() => {
        if (value === max) {
          onValueChange(min);
        } else {
          onValueChange(value + 1);
        }
      }, animationInterval);
    } else if (!isAnimating) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isAnimating, value]);

  return <Root className={classes.root} sx={sx}>
    <Slider
      className={classes.slider}
      size="small"
      step={1}
      min={min}
      max={max}
      value={value}
      onChange={handleChange}
      color="secondary"
    />
    <Box className={classes.range}>
      <Typography variant="caption" align="center" className={classes.textRange}>
        [ {value} ]
      </Typography>
      {
        !!animationInterval && <IconButton
          size="small"
          onClick={toggleAnimation}
          className={classes.iconRange}
        >
          {isAnimating ? <Pause/> : <PlayArrow/>}
        </IconButton>
      }
    </Box>
  </Root>;
};

export default AnimatedSlider;
